import { isTemplateMediaUrl, fetchMediaAsDataUrl } from './templateMedia';

function decode(src, { cors = false } = {}) {
    return new Promise((resolve, reject) => {
        const el = new Image();
        // Must be set before src, or the request goes out without the CORS mode.
        if (cors) el.crossOrigin = 'anonymous';
        el.onload = () => resolve(el);
        el.onerror = () => reject(new Error('Invalid image'));
        el.src = src;
    });
}

/**
 * Read a File/Blob/data URL/remote URL into a decoded <img> that is SAFE TO DRAW.
 *
 * Everything here is drawn onto a canvas and read back with toDataURL(), and a
 * cross-origin image fetched without CORS taints that canvas — the read then
 * throws ("Tainted canvases may not be exported"). A saved thumbnail is an R2
 * URL, not base64, so re-compressing or cropping one hits exactly that. Same
 * ladder as the exporter's loader: CORS first, then pull the bytes through our
 * own API, where same-origin settles it.
 */
export async function loadImage(source) {
    const src = typeof source === 'string'
        ? source
        : await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = () => reject(reader.error || new Error('Failed to read image'));
            reader.readAsDataURL(source);
        });

    if (/^(data:|blob:)/i.test(src)) return decode(src);

    try {
        return await decode(src, { cors: true });
    } catch (err) {
        if (!isTemplateMediaUrl(src)) throw err;
        return decode(await fetchMediaAsDataUrl(src));
    }
}

/** Compress an image File/Blob/dataURL into a small JPEG data URL for gallery thumbs. */
export async function compressImageToThumbnail(source, { maxWidth = 480, quality = 0.72 } = {}) {
    const img = await loadImage(source);

    const ratio = Math.min(1, maxWidth / (img.naturalWidth || maxWidth));
    const w = Math.max(1, Math.round((img.naturalWidth || maxWidth) * ratio));
    const h = Math.max(1, Math.round((img.naturalHeight || maxWidth) * ratio));
    const out = document.createElement('canvas');
    out.width = w;
    out.height = h;
    const ctx = out.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    return out.toDataURL('image/jpeg', quality);
}

/** Quarter turns only — anything else would need resampling to stay sharp. */
function normalizeRotation(rotation) {
    return ((Math.round((Number(rotation) || 0) / 90) * 90) % 360 + 360) % 360;
}

const clamp01 = (n) => Math.min(1, Math.max(0, Number(n) || 0));

/**
 * Cut a rectangle out of an image and compress it to a gallery thumbnail.
 *
 * `crop` is react-image-crop's PERCENT crop ({ x, y, width, height } in 0…100)
 * measured on the ROTATED picture — the same thing the dialog draws — so the
 * rectangle stays meaningful whatever the source resolution is. Rotation is
 * applied first, exactly as the dialog previews it.
 *
 * Cropping is done at the source's own resolution and only the FINAL rectangle
 * is scaled down, so a small crop out of a big photo keeps its detail.
 */
export async function cropImageToThumbnail(source, {
    crop,
    rotation = 0,
    maxWidth = 640,
    quality = 0.82,
} = {}) {
    const img = await loadImage(source);
    const nw = img.naturalWidth || img.width;
    const nh = img.naturalHeight || img.height;
    if (!nw || !nh) throw new Error('Invalid image');

    const rot = normalizeRotation(rotation);
    const swapped = rot === 90 || rot === 270;
    const rw = swapped ? nh : nw;
    const rh = swapped ? nw : nh;

    // The rotated picture, at full resolution, is what the crop rect refers to.
    let stage = img;
    if (rot) {
        const work = document.createElement('canvas');
        work.width = rw;
        work.height = rh;
        const wctx = work.getContext('2d');
        wctx.translate(rw / 2, rh / 2);
        wctx.rotate((rot * Math.PI) / 180);
        wctx.drawImage(img, -nw / 2, -nh / 2);
        stage = work;
    }

    const pct = crop && Number(crop.width) > 0 && Number(crop.height) > 0
        ? crop
        : { x: 0, y: 0, width: 100, height: 100 };
    const sx = Math.round(clamp01(pct.x / 100) * rw);
    const sy = Math.round(clamp01(pct.y / 100) * rh);
    const sw = Math.max(1, Math.min(rw - sx, Math.round(clamp01(pct.width / 100) * rw)));
    const sh = Math.max(1, Math.min(rh - sy, Math.round(clamp01(pct.height / 100) * rh)));

    const ratio = Math.min(1, maxWidth / sw);
    const w = Math.max(1, Math.round(sw * ratio));
    const h = Math.max(1, Math.round(sh * ratio));
    const out = document.createElement('canvas');
    out.width = w;
    out.height = h;
    const ctx = out.getContext('2d');
    ctx.imageSmoothingQuality = 'high';
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(stage, sx, sy, sw, sh, 0, 0, w, h);
    return out.toDataURL('image/jpeg', quality);
}
